import {EntityFactory, Reducer} from '@modifi/redux'
import {createSelector} from 'reselect'

import ms from 'ms'
import {equals} from 'ramda'
import {serviceClientFactory} from '../../lib/ownClient'
import {PLUGIN_NAME} from '../../constants/routes'

type State = Record<string, any>

export type NavigationItem = {
  type: 'sub-navigation' | 'page' | 'link'
  label: string
  description?: string
  icon?: string
  path?: string
  items?: NavigationItem[]
}

type RouterNavigationType = {
  region: string
  locale: string
  topNavigation: NavigationItem[]
  footerNavigation: NavigationItem[]
  regionsAndLocales: Record<string, Record<string, string>>
  basePath: string
  extraLinks: Partial<
    Record<'home' | 'linkedIn' | 'twitter' | 'facebook' | 'signup' | 'login', string>
  >
  footerText: string
  footerSubText: string
}

const {actions, reducer, selectors} = EntityFactory({
  actionNamePrefix: 'data/routing/navigation',
  reducerPath: ['plugins', PLUGIN_NAME, 'navigation'],
  fetchRequest: (
    state,
    params: {region: string; locale: string}
  ): Promise<RouterNavigationType> => {
    const client = serviceClientFactory()
    return client.Routing.navigation({
      region: params?.region,
      locale: params?.locale,
    }).then(res => res.data<{navigation: RouterNavigationType}>()?.navigation)
  },
  cacheKeyFunc: ({region, locale}: {region: string; locale: string}) => `${region}|${locale}`,
  cacheOptions: {
    max: 5,
    ttl: ms('1h'),
  },
})

export default reducer as Reducer

export const selectNavigationRoutes = selectors.selectEntity

export const selectFooterNavigation = (state: State): NavigationItem[] => {
  const navigationRoutes = selectNavigationRoutes(state)
  return navigationRoutes?.footerNavigation || []
}

export const selectFooterText = (state: State): string | undefined => {
  const navigationRoutes = selectNavigationRoutes(state)
  return navigationRoutes?.footerText
}

export const selectFooterSubText = (state: State): string | undefined => {
  const navigationRoutes = selectNavigationRoutes(state)
  return navigationRoutes?.footerSubText
}

export const selectExtraLinks = (state: State): Record<string, string> => {
  const navigationRoutes = selectNavigationRoutes(state)
  return navigationRoutes?.extraLinks || {}
}

export const selectBasePath = createSelector(
  selectNavigationRoutes,
  routeConfig => routeConfig?.basePath ?? '/'
)
export const selectTopNavigation = (state: State): NavigationItem[] => {
  const navigationRoutes = selectNavigationRoutes(state)
  return navigationRoutes?.topNavigation || []
}

export const selectRegionsAndLocales = createSelector(
  selectNavigationRoutes,
  routeConfig => routeConfig?.regionsAndLocales
)

export const aFetchNavigationRoutes = (region: string, locale: string): any =>
  actions?.fetch({region, locale})

export const selectFlatBasePathList = createSelector(
  selectRegionsAndLocales,
  regionAndLocales =>
    Object.values(regionAndLocales ?? {}).reduce((res, localeObj) => {
      res.push(...Object.values(localeObj))
      return res
    }, [] as string[]),
  {
    memoizeOptions: {
      resultEqualityCheck: equals,
    },
  }
)
